/**
 * Pure helpers for the self-declared menopause stage feature.
 *
 * The stage is a single string field stored on `user_progress.data`
 * (see src/logic/progressNormalize.js -> menopause_stage). Its only
 * role today is to drive UI/copy decisions on the cycle screens. As
 * symptom tracking and PFMT recommendations are layered on, every
 * call site should branch on `isMenopauseModeActive(stage)` rather
 * than duplicating the equality check.
 *
 * Staging follows the STRAW+10 reproductive ageing model, simplified
 * to the buckets women actually self-identify with on a phone screen:
 *
 *   - 'pre'    Premenopause: cycles still regular, no peri symptoms
 *   - 'peri'   Perimenopause: cycle length variable, peri symptoms
 *   - 'meno'   Menopause: <12 months since last period, no cycles
 *   - 'post'   Postmenopause: 12+ months since last period
 *   - 'unsure' Explicit "I don't know" answer
 *
 * `null` means the stage selector has never been answered. The UI
 * must treat null as "behave as before" so existing users see no
 * regression until they opt in.
 */

import { MENOPAUSE_STAGES } from './progressNormalize';

export { MENOPAUSE_STAGES };

/**
 * The stages where the cycle arc / period prediction is no longer
 * reliable and the symptom-tracking UI should take precedence.
 * Pre and unsure keep the regular cycle UI; peri keeps it but dims
 * forecasts; meno + post hide cycle predictions entirely.
 */
export const MENOPAUSE_MODE_STAGES = ['peri', 'meno', 'post'];

/**
 * Stages where cycle predictions (next period, fertile window) are
 * unreliable enough that we should not display a forecast date.
 * Peri cycles vary by 7+ days routinely (STRAW+10 -2/-1 markers),
 * meno + post have no cycles to predict.
 */
export const STAGES_WITHOUT_FORECAST = ['peri', 'meno', 'post'];

/**
 * Returns true when the user has opted in to menopause-aware UI.
 * Null (not asked) and 'pre' (regular cycles, no symptoms) and
 * 'unsure' (we don't know enough to switch modes) all return false
 * so users see the existing cycle screens unchanged.
 *
 * @param {string|null|undefined} stage
 * @returns {boolean}
 */
export function isMenopauseModeActive(stage) {
  return MENOPAUSE_MODE_STAGES.includes(stage);
}

/**
 * Returns true when cycle forecasts (next period, ovulation) should
 * be hidden because cycles are too irregular or absent to predict.
 *
 * @param {string|null|undefined} stage
 * @returns {boolean}
 */
export function shouldHideCycleForecast(stage) {
  return STAGES_WITHOUT_FORECAST.includes(stage);
}

/**
 * Coerces an arbitrary input to a valid stage string or null. Used
 * by the tile + future API entry points so a typo or stale row
 * cannot put the user in a broken UI state.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
export function coerceStage(value) {
  return MENOPAUSE_STAGES.includes(value) ? value : null;
}
